import { Grid, Alert, FormControl, FormHelperText } from '@mui/material'
import { CreateProductCatalogPriceDto } from '@services/dtos'
import SelectField from '@components/inputs/SelectField'
import InputField from '@components/inputs/InputField'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { productCatalogPriceSchema } from '@utils/schemas'
import { FormCrudHeader } from '../../FormCrudHeader'
import { useState } from 'react'
import { DateInputField } from '@components/inputs/DateInputField'
import moment from 'moment'

const VAT_OPTIONS = [
  { label: '20', value: 0.20 },
  { label: '10', value: 0.10 },
  { label: '5.5', value: 0.055 },
  { label: '2.1', value: 0.021 },
]

type Props = {
  onClose: () => void
  appendPrice: (price: CreateProductCatalogPriceDto) => void
  updatePrice: (data: CreateProductCatalogPriceDto) => void
  editingPrice?: CreateProductCatalogPriceDto | null
}

export function ProductCatalogPriceForm({ onClose, appendPrice, updatePrice, editingPrice }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { control, handleSubmit, watch, formState: { errors }, setValue } = useForm<CreateProductCatalogPriceDto>({
    resolver: yupResolver(productCatalogPriceSchema),
    defaultValues: editingPrice || {}
  })

  const onSubmitForm = async (data: CreateProductCatalogPriceDto) => {
    setIsSubmitting(true)
    editingPrice ? updatePrice(data) : appendPrice(data)
    setIsSubmitting(false)
    onClose()
  }

  return (<div className="flex w-full h-full p-4">
    <form 
      onSubmit={(e) => {
        e.stopPropagation()
        handleSubmit(onSubmitForm)(e)
      }}
      className="w-full">
      <FormCrudHeader
        isSubmitting={isSubmitting}
        onClose={onClose}
        editing={!!editingPrice}
        submitText={editingPrice ? 'Modifier' : 'Ajouter'}
      />

      <div className="max-w-screen-md mx-auto mt-4">
        <fieldset className="border border-gray-200 p-4 rounded-lg">
          <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
            Prix produit
          </legend>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputField
                control={control}
                name={'priceExclVat'}
                label={'Prix HT (€)'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                control={control}
                name="vatRate"
                label="TVA (%)"
                options={VAT_OPTIONS}
                data-cy="select-vat-rate"
              />
            </Grid>

            {watch('priceExclVat') && watch('vatRate') && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Prix TTC : {(watch('priceExclVat') * (1 + watch('vatRate'))).toFixed(2)} €
                </Alert>
              </Grid>
            )}

            {(['startAt', 'endAt'] as const).map((field) => (
              <Grid item xs={12} md={6} key={field}>
                <FormControl
                  fullWidth
                  error={!!errors[field]}
                >
                  <DateInputField
                    label={`Date ${field === 'startAt' ? 'début' : 'fin'}`}
                    name={field}
                    value={
                      watch(field)
                        ? moment(watch(field))
                        : null
                    }
                    onChange={(date) => {
                      setValue(field, date ? date.format('YYYY-MM-DD') : undefined)
                    }}
                    sx={{
                      border: errors[field]
                        ? '1px solid #d3302f'
                        : 'none',
                      borderRadius: '13px',
                    }}
                  />
                  {errors[field] && (
                    <FormHelperText error>
                      {errors[field]?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </fieldset>
      </div>
    </form>
  </div>
)}
